import {Button} from 'antd'
import classNames from 'classnames'
import {useFormikContext} from 'formik'
import useTranslate from 'modules/local/useTranslate'
import React, {useContext, useEffect} from 'react'
import {renderElse} from 'views/Shared'
import {LayoutContext} from '../layouts/Default/LayoutContext'

function FormActionBar({
                           cancelSubmit,
                           alwaysVisible = true,
                           submitColor,
                           submitText = 'save',
                           resetText = 'reset',
                           actions,
                           className,
                           children,
                       }) {
    const t = useTranslate()
    const {
        touched,
        isValid,
        isSubmitting,
        handleSubmit,
        dirty,
        resetForm,
        initialValues,
        setSubmitting,
        setFieldValue
    } = useFormikContext()

    const show = dirty && Object.keys(touched).length > 0
    const submit = () => {
        // Không tự setSubmitting(false) ngay sau handleSubmit() — làm vậy sẽ
        // huỷ isSubmitting:true của Formik ngay trong cùng tick (trước khi
        // React kịp render), khiến nút submit không bao giờ hiện loading và
        // có thể bị bấm liên tục. Để Formik tự quản lý isSubmitting.
        if (isSubmitting) return
        handleSubmit()
    }
    useEffect(() => {
        if (isSubmitting && cancelSubmit) {
            setSubmitting(false)
        }
    }, [cancelSubmit, isSubmitting])

    if (children)
        return children(
            <div
                style={{
                    borderRadius: '0.3em',
                }}
                className={classNames(
                    'flex   pointer-events-auto background-100 justify-end p-2  ',
                    className,
                    !alwaysVisible && {
                        'fadeInUp faster opacity-0 pointer-events-auto animated': show,
                        fadeOutDown: !show,
                    }
                )}>
                {actions ? (
                    actions
                ) : (
                    <div className="flex items-center gap-4">
                        <span
                            className="mr-3 font-semibold text-color-300 hover:text-color-000 cursor-pointer"
                            onClick={
                                isSubmitting
                                    ? undefined
                                    : () => {
                                        resetForm({
                                            values: {...initialValues, resetKey: Date.now()},
                                        })
                                    }
                            }>
                          {t(resetText)}
                        </span>
                        <Button
                            color={submitColor}
                            disabled={!isValid || isSubmitting}
                            loading={isSubmitting}
                            onClick={submit}
                            type="primary"
                            className="font-semibold button-rounded-lg">
                            {t(submitText)}
                        </Button>
                    </div>
                )}
            </div>
        )
    return (
        <div
            style={{zIndex: 11}}
            className={classNames(
                'w-full py-2 sticky bottom-0 pointer-events-none',
                className
            )}>
            <div
                className={classNames(
                    'flex items-center w-full pointer-events-auto justify-end p-2 background rounded-xl',
                    !alwaysVisible && {
                        'opacity-0 faster fadeInUp  animated': alwaysVisible || show,
                        fadeOutDown: !(alwaysVisible || show),
                    }
                )}>
                {actions ? (
                    actions
                ) : (
                    <div className="flex items-center gap-4">
                        <span
                            className="font-semibold text-color-300 hover:text-color-000 cursor-pointer"
                            onClick={
                                isSubmitting
                                    ? undefined
                                    : () => {
                                        resetForm({
                                            values: {...initialValues, resetKey: Date.now()},
                                        })
                                    }
                            }>
                          {t(resetText)}
                        </span>
                        <Button
                            color={submitColor}
                            disabled={!isValid || !dirty || isSubmitting}
                            loading={isSubmitting}
                            onClick={submit}
                            type="primary"
                            className="font-semibold button-rounded-lg">
                            {t(submitText)}
                        </Button>
                    </div>
                )}
            </div>
        </div>
    )
}

export const ResetButton = ({
                                style,
                                className,
                                label = 'reset',
                                disabled = false,
                                ...props
                            }) => {
    const t = useTranslate()
    const {isSm} = useContext(LayoutContext)
    const {initialValues, resetForm, isSubmitting, setFieldValue} = useFormikContext()
    const handleReset = () => {
        resetForm({
            values: {...initialValues, resetKey: Date.now()},
        })
    }

    // Mobile: nút secondary rõ ràng, to, dễ chạm — không dùng text link như desktop
    if (isSm) {
        return (
            <Button
                size="large"
                type="default"
                style={{minHeight: 44, ...style}}
                className={classNames('flex-1 font-semibold button-rounded-lg', className)}
                disabled={disabled || isSubmitting}
                onClick={handleReset}
                {...props}>
                {t(label)}
            </Button>
        )
    }

    return (
        <span
            style={style}
            className={classNames(
                'mr-3 font-bold hover:underline',
                !disabled && 'cursor-pointer',
                className
            )}
            onClick={disabled || isSubmitting ? undefined : handleReset}
            {...props}>
      {t(label)}
    </span>
    )
}

export const SubmitButton = ({
                                 label = 'submit',
                                 dirtyRequired = true,
                                 className,
                                 style,
                                 ...props
                             }) => {
    const t = useTranslate()
    const {isSm} = useContext(LayoutContext)
    const {dirty, isValid, isSubmitting, handleSubmit} =
        useFormikContext()
    const submit = () => {
        // props.loading cho phép caller (vd nút gắn với useAsyncAction) khoá
        // nút bằng trạng thái request thật; isSubmitting là fallback của Formik.
        // Không tự setSubmitting(false) ở đây — xem giải thích ở FormActionBar.submit.
        if (props.loading || isSubmitting) {
            return
        }
        handleSubmit()
    }
    const is_modified = dirtyRequired ? dirty : true
    return (
        <Button
            size="large"
            type="primary"
            style={isSm ? {minHeight: 44, ...style} : style}
            className={classNames(
                'button-rounded-lg font-semibold',
                isSm ? 'flex-1 px-6' : 'px-6 py-1',
                (!isValid || !is_modified) && 'pointer-events-none',
                className
            )}
            onClick={submit}
            loading={isSubmitting}
            disabled={!isValid || !is_modified}
            {...props}>
            {t(label)}
        </Button>
    )
}

export const FormActionGroups = ({
                                     style,
                                     className,
                                     isSubmitting,
                                     resetProps = () => ({}),
                                     submitProps = () => ({}),
                                 }) => {
    const {isSm} = useContext(LayoutContext)
    const {hidden: hide_reset = false, ...reset_props} = resetProps ?? {}
    const {hidden: hide_submit = false, ...submit_props} = submitProps ?? {}
    return (
        <div
            style={style}
            className={classNames(
                'flex items-center background',
                isSm ? 'gap-3 p-3' : 'justify-end gap-2 p-2',
                className
            )}>
            {renderElse(
                hide_reset,
                <ResetButton
                    disabled={isSubmitting}
                    {...reset_props}
                />
            )}
            {renderElse(
                hide_submit,
                <SubmitButton
                    loading={isSubmitting}
                    {...submit_props}
                />
            )}
        </div>
    )
}

export default FormActionBar
